import {useEffect} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../_metronic/layout/core'

type FaqItem = {
  id: string
  question: string
  answer: JSX.Element
}

type FaqSection = {
  id: string
  title: string
  items: FaqItem[]
}

const FAQ_SECTIONS: FaqSection[] = [
  {
    id: 'general',
    title: 'General Questions',
    items: [
      {
        id: 'what-is-maestrox',
        question: 'What is MaestroX?',
        answer: (
          <>
            <p>
              MaestroX is a modern title search operations platform that connects Title Companies
              and Search Vendors through two purpose-built portals:
            </p>
            <ul>
              <li>
                <strong>MaestroX CONNECT (Client Portal):</strong> for Companies to order, track,
                manage communications with vendors, and receive searches.
              </li>
              <li>
                <strong>MaestroX FLOW (Searcher/Vendor Portal):</strong> for Search Vendors to
                accept assignments, upload deliverables, communicate, and complete work.
              </li>
            </ul>
            <p className='mb-0'>
              Together, CONNECT + FLOW reduce manual effort, standardize delivery, and create a
              consistent operating model for ordering, production, and fulfillment across your
              entire vendor network.
            </p>
          </>
        ),
      },
      {
        id: 'is-maestrox-free',
        question: 'Is MaestroX free?',
        answer: (
          <>
            <p>
              MaestroX is a subscription-based platform. Pricing is based on order volume and the
              solutions enabled (ex: CONNECT ordering, workflow automation, integrations, and
              MaestroX search products).
            </p>
            <p className='mb-0'>To review pricing and options, please schedule a demo with our team.</p>
          </>
        ),
      },
      {
        id: 'how-to-access',
        question: 'How do I access CONNECT or FLOW?',
        answer: (
          <>
            <p>
              Both CONNECT and FLOW are web-based portals and require internet access plus an
              active MaestroX account.
            </p>
            <ul>
              <li>
                <strong>CONNECT:</strong>{' '}
                <a href='https://portal.maestrox.com/auth' target='_blank' rel='noreferrer'>
                  https://portal.maestrox.com/auth
                </a>
              </li>
              <li>
                <strong>FLOW:</strong>{' '}
                <a href='https://searcher.maestrox.com/auth' target='_blank' rel='noreferrer'>
                  https://searcher.maestrox.com/auth
                </a>
              </li>
            </ul>
            <p className='mb-0'>Once registered, your account is reviewed and enabled by our team.</p>
          </>
        ),
      },
    ],
  },
  {
    id: 'account-login',
    title: 'Account and Login',
    items: [
      {
        id: 'forgot-password',
        question: 'What should I do if I forget my password?',
        answer: (
          <p className='mb-0'>
            On the login page, select <strong>“Forgot Password”</strong> and follow the steps.
            You’ll receive password reset instructions via your registered email address.
          </p>
        ),
      },
      {
        id: 'multiple-devices',
        question: 'Can I use CONNECT or FLOW on multiple devices?',
        answer: (
          <p className='mb-0'>
            Yes — both portals are browser-based and can be accessed from multiple devices using
            your credentials, provided you have internet access.
          </p>
        ),
      },
    ],
  },
  {
    id: 'connect-features',
    title: 'CONNECT (Client Portal) – Features & Functionality',
    items: [
      {
        id: 'what-can-i-do-in-connect',
        question: 'What can I do in CONNECT?',
        answer: (
          <>
            <p>
              CONNECT is designed for Companies to manage the full lifecycle of title search work.
              Key functions include:
            </p>
            <ul className='mb-0'>
              <li>Create and submit orders directly through the portal</li>
              <li>Real-time order status visibility across all states and search types</li>
              <li>Standardized delivery outcomes through consistent workflows</li>
              <li>Automated routing to vendors (where applicable based on rules and setup)</li>
              <li>Centralized document access for completed work and attachments</li>
              <li>Operational consistency across internal and third-party workflows</li>
              <li>Reporting and visibility into volume, turnaround times, and exceptions</li>
              <li>Use integrations to receive and deliver searches</li>
            </ul>
          </>
        ),
      },
      {
        id: 'search-types',
        question: 'Can CONNECT support multiple search types (Sync, SMART, County, etc.)?',
        answer: (
          <p className='mb-0'>
            Yes — CONNECT supports MaestroX search products and workflows, including scenarios that
            may involve County searches, SyncSearch, PreExam, and TPS orders depending on your
            system configuration and integrations.
          </p>
        ),
      },
      {
        id: 'communicate-with-vendor',
        question: 'Does CONNECT allow us to communicate directly with the search vendor/searcher?',
        answer: (
          <p className='mb-0'>
            Yes. CONNECT supports structured collaboration and order status updates through the
            MaestroX workflow model via the <strong>notes</strong> section. For vendor-side work
            completion and file submission, FLOW is used on the searcher side.
          </p>
        ),
      },
    ],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    items: [
      {
        id: 'tps-integration',
        question: 'Does MaestroX integrate with Title Production Software?',
        answer: (
          <>
            <p>
              Yes. MaestroX integrates with multiple Title Production Software platforms to support
              ordering, status updates, and delivery workflows. Supported integrations vary based
              on the platform and configuration.
            </p>
            <p className='mb-0'>
              For the current list of supported integrations, visit our website or contact your
              MaestroX representative.
            </p>
          </>
        ),
      },
    ],
  },
  {
    id: 'security-privacy',
    title: 'Security & Privacy',
    items: [
      {
        id: 'is-data-safe',
        question: 'Is my data safe with MaestroX?',
        answer: (
          <>
            <p>
              Yes. MaestroX takes privacy and security seriously and uses industry-standard
              protections, including:
            </p>
            <ul>
              <li>Encrypted data in transit (SSL/TLS)</li>
              <li>Encryption at rest (AWS disk encryption)</li>
              <li>Role-based access controls and audit-friendly workflows</li>
            </ul>
            <p className='mb-0'>More information is available upon request.</p>
          </>
        ),
      },
      {
        id: 'delete-account',
        question: 'Can I delete my account or remove data?',
        answer: (
          <>
            <p>
              Yes. To request account deletion or data removal, email{' '}
              <a href='mailto:support@maestrox.com'>support@maestrox.com</a> with your request.
            </p>
            <p className='mb-0'>
              <strong>Please note:</strong> deletion requests are permanent and may impact
              audit/history retention depending on contractual and operational requirements.
            </p>
          </>
        ),
      },
      {
        id: 'privacy-compliance',
        question: 'Does MaestroX comply with privacy regulations like CCPA?',
        answer: (
          <p className='mb-0'>
            MaestroX follows modern privacy and data protection practices aligned with applicable
            requirements. If a formal compliance review is needed, documentation can be provided
            through the appropriate channel.
          </p>
        ),
      },
    ],
  },
  {
    id: 'support',
    title: 'Troubleshooting & Support',
    items: [
      {
        id: 'encounter-error',
        question: 'What should I do if I encounter an error in CONNECT or FLOW?',
        answer: (
          <>
            <p>
              If you experience an error, email{' '}
              <a href='mailto:support@maestrox.com'>support@maestrox.com</a> and include:
            </p>
            <ul className='mb-0'>
              <li>Portal name (CONNECT or FLOW)</li>
              <li>Order number (if applicable)</li>
              <li>Brief description of the issue</li>
              <li>Screenshots (if available)</li>
            </ul>
          </>
        ),
      },
      {
        id: 'contact-support',
        question: 'How do I contact support?',
        answer: (
          <>
            <p>
              Support is available by email at{' '}
              <a href='mailto:support@maestrox.com'>support@maestrox.com</a>.
            </p>
            <p className='mb-0'>
              <strong>Hours:</strong> Monday–Friday, 9:00 AM – 5:30 PM Eastern.
            </p>
          </>
        ),
      },
      {
        id: 'feedback',
        question: 'How can I provide feedback or suggest new features?',
        answer: (
          <p className='mb-0'>
            We welcome feedback. Please submit feature requests to{' '}
            <a href='mailto:support@maestrox.com'>support@maestrox.com</a> or through your MaestroX
            representative so we can evaluate impact, priority, and roadmap alignment.
          </p>
        ),
      },
    ],
  },
  {
    id: 'subscription-billing',
    title: 'Subscription & Billing',
    items: [
      {
        id: 'pricing',
        question: 'What are the pricing plans for MaestroX?',
        answer: (
          <>
            <p>Pricing varies by customer type and configuration:</p>
            <ul>
              <li>Title Company subscription plans (CONNECT users)</li>
              <li>Search Vendor participation/pricing models (FLOW users, if applicable)</li>
            </ul>
            <p className='mb-0'>
              Your MaestroX representative will confirm the correct schedule during onboarding or
              renewal.
            </p>
          </>
        ),
      },
      {
        id: 'cancel-subscription',
        question: 'Can I cancel my subscription at any time?',
        answer: (
          <p className='mb-0'>
            Yes. Subscription cancellations require <strong>30 days’ notice</strong>, unless
            otherwise defined in your service agreement.
          </p>
        ),
      },
    ],
  },
  {
    id: 'updates',
    title: 'Updates and New Versions',
    items: [
      {
        id: 'get-updates',
        question: 'How do I get the latest MaestroX updates?',
        answer: (
          <>
            <p>
              MaestroX is a web-based platform, so updates are delivered automatically. No app
              store downloads are required.
            </p>
            <p>When enhancements are released:</p>
            <ul className='mb-0'>
              <li>Users receive updates automatically in CONNECT and FLOW</li>
              <li>Major changes may be communicated through release notes or announcements</li>
            </ul>
          </>
        ),
      },
      {
        id: 'data-loss',
        question: 'Will I lose my data during updates?',
        answer: (
          <p className='mb-0'>
            No. Your data is preserved during platform updates. MaestroX follows controlled
            deployment practices to protect order history, documents, and workflow records.
          </p>
        ),
      },
    ],
  },
]

const FaqsPage = () => {
  useEffect(() => {
    document.getElementById('kt_layout_toolbar')?.classList.remove('d-none')
    return () => {
      document.getElementById('kt_layout_toolbar')?.classList.add('d-none')
    }
  }, [])

  return (
    <>
      <PageTitle breadcrumbs={[]}>FAQS</PageTitle>
      <div className='card bg-white min-h-750px' style={{border: 'solid 1px #E8E8E8'}}>
        <div className='card-header pt-3 pb-2 d-flex align-items-center'>
          <h3 className='card-title align-items-start flex-row'>
            <span className='card-label fw-light fs-3 text-primary'>Frequently Asked Questions</span>
          </h3>
        </div>

        <div className='card-body py-6'>
          {FAQ_SECTIONS.map((section) => {
            const accordionId = `faq-accordion-${section.id}`
            return (
              <div key={section.id} className='mb-8'>
                <h4 className='fw-semibold fs-4 text-primary mb-4'>{section.title}</h4>
                <div className='accordion' id={accordionId}>
                  {section.items.map((item) => {
                    const collapseId = `faq-${section.id}-${item.id}`
                    const headingId = `heading-${collapseId}`
                    return (
                      <div key={item.id} className='accordion-item bg-white'>
                        <h2 className='accordion-header' id={headingId}>
                          <button
                            className='accordion-button collapsed fw-semibold fs-6'
                            type='button'
                            data-bs-toggle='collapse'
                            data-bs-target={`#${collapseId}`}
                            aria-expanded='false'
                            aria-controls={collapseId}
                          >
                            {item.question}
                          </button>
                        </h2>
                        <div
                          id={collapseId}
                          className='accordion-collapse collapse'
                          aria-labelledby={headingId}
                          data-bs-parent={`#${accordionId}`}
                        >
                          <div className='accordion-body text-gray-700 fs-6'>{item.answer}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

const FaqsWrapper = () => {
  const intl = useIntl()
  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.DASHBOARD'})}</PageTitle>
      <FaqsPage />
    </>
  )
}

export {FaqsWrapper}
